import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, MapPin, Star, Sparkles, ArrowRight, DollarSign } from 'lucide-react';
import { getOffMarketStayById } from '../services/offMarketStaysService';
import { getOffMarketSpecialCommissionBreakdown } from '../utils/offMarketSpecialCommission';

const DARK = '#111111';
const CHARCOAL = '#3a3a3a';

function getCoverImages(property) {
    const imgs = property?.adminImages;
    if (!Array.isArray(imgs) || imgs.length === 0) return [];
    const sorted = [...imgs].sort((a, b) => {
        if (a.categoryCode === 1) return -1;
        if (b.categoryCode === 1) return 1;
        return 0;
    });
    return sorted
        .map((img) => img.s3Url || img.sabreImageUrl)
        .filter(Boolean)
        .slice(0, 4);
}

function locationLabel(property) {
    const city = property?.location?.city;
    const country = property?.location?.countryName;
    return [city, country].filter(Boolean).join(', ');
}

function getScenario(hasPerks, hasCommission) {
    if (hasPerks && hasCommission) return 'both';
    if (hasPerks) return 'perks';
    if (hasCommission) return 'commission';
    return 'none';
}

function getScenarioContent(scenario) {
    switch (scenario) {
        case 'both':
            return {
                hook: 'This stay includes AXONALL Perks for your clients and an elevated commission rate for you.',
                cta: 'View Rooms, Perks & Rates',
                secondaryBadge: 'Perks & Commission',
            };
        case 'perks':
            return {
                hook: 'This stay includes AXONALL Perks—exclusive privileges negotiated for your clients.',
                cta: 'View Rooms & AXONALL Perks',
                secondaryBadge: 'AXONALL Perks',
            };
        case 'commission':
            return {
                hook: 'This stay offers an elevated advisor commission above the standard program rate.',
                cta: 'View Rooms & Commission',
                secondaryBadge: 'Special Commission',
            };
        default:
            return {
                hook: 'Reserved exclusively for Axonall Reserve advisors—not available on the open market.',
                cta: 'Explore Rooms & Rates',
                secondaryBadge: null,
            };
    }
}

function PerkRow({ title, description }) {
    return (
        <div className="flex gap-4 py-4 border-b border-gray-100 last:border-0">
            <div className="w-px shrink-0 self-stretch bg-gray-200" />
            <div className="min-w-0 flex-1">
                <p className="text-[11px] font-poppinsRegular uppercase tracking-[0.14em] leading-snug" style={{ color: DARK }}>
                    {title}
                </p>
                {description && (
                    <p className="text-xs font-poppinsLight text-gray-500 mt-1.5 leading-relaxed">{description}</p>
                )}
            </div>
        </div>
    );
}

function PerksSection({ perks, compact = false }) {
    return (
        <section className={compact ? 'mb-5' : 'mb-6'}>
            <div className="flex items-end justify-between gap-4 mb-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: CHARCOAL }} />
                        <span className="text-[10px] tracking-[0.28em] uppercase font-poppinsRegular" style={{ color: DARK }}>
                            AXONALL Perks
                        </span>
                    </div>
                    <p className="font-poppinsLight text-sm text-gray-500 leading-relaxed max-w-sm">
                        Exclusive privileges for your clients on this stay.
                    </p>
                </div>
                <span className="shrink-0 text-[10px] font-poppinsLight text-gray-400 uppercase tracking-wider tabular-nums">
                    {perks.length} included
                </span>
            </div>
            <div className="border border-gray-100 bg-[#fafafa] px-5">
                {perks.map((perk, i) => (
                    <PerkRow key={i} title={perk.title} description={perk.description} />
                ))}
            </div>
        </section>
    );
}

function CommissionSection({ commissionBreakdown, featured = false }) {
    if (featured) {
        return (
            <section className="mb-6">
                <div className="flex items-end justify-between gap-4 mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="w-3.5 h-3.5 text-amber-700" />
                            <span className="text-[10px] tracking-[0.28em] uppercase font-poppinsRegular text-amber-900">
                                Special Commission
                            </span>
                        </div>
                        <p className="font-poppinsLight text-sm text-gray-500 leading-relaxed max-w-sm">
                            Elevated advisor commission on this off-market stay.
                        </p>
                    </div>
                </div>
                <div className="border border-amber-200/70 bg-amber-50/40 px-5 py-5">
                    <p className="font-AXONALLPrimeRegular text-3xl text-amber-900 leading-none mb-1">
                        {commissionBreakdown.total}%
                    </p>
                    <p className="font-poppinsRegular text-sm mb-2" style={{ color: DARK }}>
                        Commission on this booking
                    </p>
                    {commissionBreakdown.specialAdded > 0 && (
                        <p className="text-xs font-poppinsLight text-amber-800/80 tabular-nums">
                            {commissionBreakdown.defaultBase}% base + {commissionBreakdown.specialAdded}% off-market uplift
                        </p>
                    )}
                </div>
            </section>
        );
    }

    return (
        <div className="mb-6 pl-4 border-l-2 border-amber-400/80">
            <p className="text-[10px] tracking-[0.22em] uppercase font-poppinsLight text-gray-500 mb-1">
                Special commission
            </p>
            <p className="font-poppinsRegular text-sm" style={{ color: DARK }}>
                <span className="text-lg font-AXONALLPrimeRegular text-amber-800">{commissionBreakdown.total}%</span>
                {' '}on this booking
                {commissionBreakdown.specialAdded > 0 && (
                    <span className="block text-xs font-poppinsLight text-gray-500 mt-1">
                        {commissionBreakdown.defaultBase}% + {commissionBreakdown.specialAdded}% off-market uplift
                    </span>
                )}
            </p>
        </div>
    );
}

function ExclusiveAccessSection() {
    return (
        <section className="mb-6 border border-gray-100 bg-[#fafafa] px-5 py-5">
            <p className="text-[10px] tracking-[0.28em] uppercase font-poppinsRegular mb-2" style={{ color: DARK }}>
                Exclusive Off-Market Access
            </p>
            <p className="font-poppinsLight text-sm text-gray-500 leading-relaxed">
                This property is curated for Axonall Reserve advisors only. It is not listed on the open market and is available through your Studio access.
            </p>
        </section>
    );
}

const OffMarketStayBanner = ({ offMarketPropertyId, propertyData = null, onClose }) => {
    const [property, setProperty] = useState(propertyData || null);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(false);
    const [imgIdx, setImgIdx] = useState(0);

    useEffect(() => {
        if (propertyData) {
            setProperty(propertyData);
            setLoading(false);
            return;
        }
        if (!offMarketPropertyId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getOffMarketStayById(offMarketPropertyId);
                if (!cancelled && res?.success && res.data) setProperty(res.data);
            } catch { /* skip */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [offMarketPropertyId, propertyData]);

    useEffect(() => {
        if (!loading && property) {
            const t = setTimeout(() => setVisible(true), 80);
            return () => clearTimeout(t);
        }
    }, [loading, property]);

    const images = property ? getCoverImages(property) : [];
    useEffect(() => {
        if (images.length < 2) return;
        const t = setInterval(() => setImgIdx((i) => (i + 1) % images.length), 6000);
        return () => clearInterval(t);
    }, [images.length]);

    const handleClose = useCallback(() => {
        setVisible(false);
        setTimeout(() => onClose?.(), 280);
    }, [onClose]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') handleClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [handleClose]);

    const hasPerks = property?.axonallPerks?.enabled && Array.isArray(property?.axonallPerks?.items) && property.axonallPerks.items.length > 0;
    const perks = hasPerks ? property.axonallPerks.items : [];
    const hasCommission = property?.specialCommission?.enabled && property?.specialCommission?.percentage != null;
    const commissionBreakdown = hasCommission
        ? getOffMarketSpecialCommissionBreakdown(property.specialCommission)
        : null;

    const scenario = getScenario(hasPerks, hasCommission);
    const content = useMemo(() => getScenarioContent(scenario), [scenario]);

    if (!offMarketPropertyId || (!loading && !property)) return null;

    const rating = property?.sabreRating ? Number(property.sabreRating) : null;
    const loc = property ? locationLabel(property) : '';
    const coverSrc = images[imgIdx] || 'https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg?auto=compress&cs=tinysrgb&w=1200';

    return (
        <div
            className={`fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{ backgroundColor: 'rgba(17,17,17,0.6)' }}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label="Off-market stay"
        >
            <div
                className={`relative w-full max-w-4xl bg-white overflow-hidden flex flex-col lg:flex-row max-h-[88vh] min-h-0 transition-all duration-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            >
                <div className="relative w-full lg:w-[46%] shrink-0 aspect-[5/4] lg:aspect-auto lg:min-h-[520px] overflow-hidden bg-neutral-100">
                    {!loading && (
                        <img
                            key={imgIdx}
                            src={coverSrc}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 lg:bg-gradient-to-r lg:from-transparent lg:to-black/20" />

                    <div className="absolute top-5 left-5 z-10 flex flex-wrap gap-2">
                        <span className="text-[10px] font-poppinsRegular uppercase tracking-[0.22em] text-white/90 bg-black/25 backdrop-blur-sm px-3 py-1.5 border border-white/15">
                            Off-Market
                        </span>
                        {content.secondaryBadge && (
                            <span className="text-[10px] font-poppinsRegular uppercase tracking-[0.18em] text-gray-900 bg-white/90 backdrop-blur-sm px-3 py-1.5">
                                {content.secondaryBadge}
                            </span>
                        )}
                    </div>

                    {images.length > 1 && (
                        <div className="absolute bottom-5 left-5 right-5 z-10 flex gap-1">
                            {images.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setImgIdx(i)}
                                    className={`h-px flex-1 max-w-8 transition-all duration-300 ${i === imgIdx ? 'bg-white' : 'bg-white/35 hover:bg-white/60'}`}
                                    aria-label={`Photo ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}

                    <div className="absolute bottom-5 left-5 right-5 z-10 lg:hidden pointer-events-none">
                        {!loading && property && (
                            <h2 className="font-AXONALLPrimeRegular text-xl uppercase tracking-wide text-white leading-snug line-clamp-2 drop-shadow-sm">
                                {property.name}
                            </h2>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-h-0 min-w-0 flex flex-col bg-white">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors lg:bg-white/90"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 sm:px-8 pt-7 pb-4 lg:pt-9">
                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="w-5 h-5 border border-gray-200 border-t-gray-900 rounded-full animate-spin" />
                            </div>
                        ) : property ? (
                            <>
                                <p className="text-[10px] tracking-[0.32em] uppercase font-poppinsLight mb-2" style={{ color: CHARCOAL }}>
                                    Axonall Reserve
                                </p>
                                <div className="w-8 h-px mb-5" style={{ backgroundColor: CHARCOAL }} />

                                <h2 className="hidden lg:block font-AXONALLPrimeRegular text-2xl uppercase tracking-wide leading-snug pr-8 mb-3" style={{ color: DARK }}>
                                    {property.name}
                                </h2>

                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm">
                                    {loc && (
                                        <span className="inline-flex items-center gap-1.5 font-poppinsLight text-gray-500">
                                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                                            {loc}
                                        </span>
                                    )}
                                    {rating != null && (
                                        <span className="inline-flex items-center gap-1 font-poppinsRegular text-gray-700">
                                            <Star className="w-3.5 h-3.5 text-amber-600 fill-amber-600" />
                                            {rating.toFixed(1)}
                                        </span>
                                    )}
                                </div>

                                <p className="font-poppinsLight text-sm text-gray-500 leading-relaxed mb-6 max-w-md">
                                    {content.hook}
                                </p>

                                {scenario === 'both' && (
                                    <>
                                        <PerksSection perks={perks} compact />
                                        <CommissionSection commissionBreakdown={commissionBreakdown} featured={false} />
                                    </>
                                )}

                                {scenario === 'perks' && (
                                    <PerksSection perks={perks} />
                                )}

                                {scenario === 'commission' && (
                                    <CommissionSection commissionBreakdown={commissionBreakdown} featured />
                                )}

                                {scenario === 'none' && (
                                    <ExclusiveAccessSection />
                                )}
                            </>
                        ) : null}
                    </div>

                    {!loading && property && (
                        <div className="shrink-0 px-6 sm:px-8 py-5 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="w-full py-3.5 text-white text-[11px] tracking-[0.26em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                                style={{ backgroundColor: DARK }}
                            >
                                {content.cta}
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OffMarketStayBanner;
